import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback list shown only if the API call fails. Real source of truth is the
// admin-editable list at /api/transactions/bounce-codes.
const FALLBACK_BOUNCE_CODES = [
  { code: 'R01', label: 'Insufficient Funds (NSF)', default_fee: true, default_fee_amount: 35 },
  { code: 'R08', label: 'Payment Stopped', default_fee: true, default_fee_amount: 100 },
  { code: 'R00', label: 'Administrative / No Fee', default_fee: false, default_fee_amount: 0 },
];

export default function BounceTransactionDialog({ open, onOpenChange, transaction, onSubmit }) {
  const { getAuthHeaders } = useAuth();
  const [bounceCodes, setBounceCodes] = useState(FALLBACK_BOUNCE_CODES);
  const [code, setCode] = useState('R01');
  const [chargeFee, setChargeFee] = useState(true);
  const [feeAmount, setFeeAmount] = useState('35');
  const [bounceDate, setBounceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reload codes every time the dialog opens so admin edits show up immediately
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/transactions/bounce-codes`, { headers: getAuthHeaders() });
        if (!cancelled && Array.isArray(res.data) && res.data.length) {
          setBounceCodes(res.data);
        }
      } catch (err) {
        // Keep fallback list — bounce flow stays usable even if the codes
        // endpoint is unreachable.
      }
    })();
    return () => { cancelled = true; };
  }, [open, getAuthHeaders]);

  useEffect(() => {
    if (open) {
      // Pre-fill bounce date with the original payment's date so the
      // ACH_RETURN lines up with the actual bank settlement date.
      let defaultDate = new Date().toISOString().slice(0, 10);
      const txDate = transaction?.date;
      if (txDate) {
        // transaction.date may be ISO datetime or 'YYYY-MM-DD'
        const d = new Date(txDate);
        if (!isNaN(d.getTime())) {
          defaultDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
      }
      setCode('R01');
      setChargeFee(true);
      // Pre-fill from R01's configured default (35 in seed; admin can override).
      const r01 = bounceCodes.find((c) => c.code === 'R01');
      setFeeAmount(String(r01?.default_fee_amount ?? 35));
      setBounceDate(defaultDate);
      setNotes('');
    }
  }, [open, transaction, bounceCodes]);

  // When code changes, sync chargeFee AND fee amount with that code's defaults.
  // R08 (Payment Stopped) carries $100; everything else $35 unless admin-edited.
  const handleCodeChange = (newCode) => {
    setCode(newCode);
    const meta = bounceCodes.find((c) => c.code === newCode);
    if (meta) {
      setChargeFee(meta.default_fee);
      if (meta.default_fee_amount !== undefined && meta.default_fee_amount !== null) {
        setFeeAmount(String(meta.default_fee_amount));
      }
    }
  };

  const handleSubmit = async () => {
    if (!transaction) return;
    setSubmitting(true);
    try {
      await onSubmit({
        transaction_id: transaction.id,
        return_code: code,
        charge_fee: chargeFee,
        fee_amount: chargeFee ? parseFloat(feeAmount) || 0 : 0,
        bounce_date: bounceDate,
        notes,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const meta = bounceCodes.find((c) => c.code === code);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="bounce-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            Bounce Payment
          </DialogTitle>
          <DialogDescription>
            Mark this payment as bounced. An ACH_RETURN entry will reverse the payment and an optional bounce fee can be added.
          </DialogDescription>
        </DialogHeader>

        {transaction && (
          <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
            <div><span className="text-slate-500">Date:</span> {transaction.date}</div>
            <div><span className="text-slate-500">Amount:</span> <span className="font-semibold">{formatCurrency(transaction.amount)}</span></div>
            <div><span className="text-slate-500">Reference:</span> <span className="font-mono text-xs">{transaction.reference || transaction.reference_number || '—'}</span></div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="bounce-code">Return Code</Label>
            <Select value={code} onValueChange={handleCodeChange}>
              <SelectTrigger id="bounce-code" data-testid="bounce-code-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {bounceCodes.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`bounce-code-${c.code}`}>
                    <span className="font-mono text-xs mr-2">{c.code}</span>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {meta && !meta.default_fee && (
              <p className="text-xs text-amber-700 mt-1">This return code typically does not carry a fee.</p>
            )}
          </div>

          <div className="flex items-center gap-2 p-3 rounded border border-slate-200">
            <input
              type="checkbox"
              id="charge-fee"
              checked={chargeFee}
              onChange={(e) => setChargeFee(e.target.checked)}
              className="h-4 w-4"
              data-testid="charge-fee-checkbox"
            />
            <Label htmlFor="charge-fee" className="cursor-pointer flex-1">Charge bounce fee</Label>
            {chargeFee && (
              <div className="flex items-center gap-1">
                <span className="text-sm">$</span>
                <Input
                  value={feeAmount}
                  onChange={(e) => setFeeAmount(e.target.value)}
                  className="w-20 h-8"
                  type="number"
                  step="0.01"
                  data-testid="fee-amount-input"
                />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="bounce-date">Bounce Date</Label>
            <Input
              id="bounce-date"
              type="date"
              value={bounceDate}
              onChange={(e) => setBounceDate(e.target.value)}
              data-testid="bounce-date-input"
            />
          </div>

          <div>
            <Label htmlFor="bounce-notes">Notes (optional)</Label>
            <Input
              id="bounce-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Customer notified by email"
              data-testid="bounce-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-red-600 hover:bg-red-700" data-testid="bounce-submit-btn">
            <AlertCircle className="h-4 w-4 mr-2" />
            {submitting ? 'Bouncing…' : 'Bounce Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
